import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
//
import AlertDialog from '../../componets/dialog/alertDialog';
import { FormButton } from '../../componets/form/formButton';
import { paths } from '../../config';
import { useContextGlobal } from '../../context/ContextGlobal';
import { useToast } from '../../context/ToastContext';
import api from '../../services/api';
import { handleExceptionMessage } from '../../util/handleExceptionAxios';
import { message } from '../../util/handleMessages';
import { GenericEnum, typeUserOptions } from '../../communs/enums/generic-enum';
import { FormInputProps } from '../../componets/form/formInterfaces';
import RenderForm from '../../componets/form/renderForm';
import IDescription from '../../util/interfaces/description';
import { useAuth } from '../../context/AuthContext';
import { IconComponent, icons } from '../../componets/icons';

interface IFormInput {
    name: string;
    userName: string;
    email: string;
    typeUser: {
        description: string;
        id: string;
    };
    company: {
        description: string;
        id: string;
    }[];
    roles: {
        description: string;
        id: string;
    }[];
    active: boolean;
    verifiedEmail: boolean;
    password?: string;
    passwordConfirmation?: string;
}

interface IUpdateAutocomplete {
    ids: string[];
    keyField: 'role';
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

const defaultValues = {
    name: '',
    userName: '',
    email: '',
    typeUser: { id: 'user', description: 'Usuário' },
    active: true,
    roles: [],
    company: undefined,
    verifiedEmail: false,
};

const typeUserInstance = new GenericEnum(typeUserOptions);
const typeUserEnum = typeUserInstance.optionsList();

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}>
            {value === index && (
                <Box pt={3}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const Form: React.FC<{ settings: any }> = props => {
    const { settings } = props;
    const history = useHistory();
    const params = useParams<'id' | any>();
    const { addToast } = useToast();
    const { setOpenLoading } = useContextGlobal();
    const [openModalDelete, setOpenModalDelete] = useState(false);
    const [optionsRole, setOptionsRole] = useState<
        { description: string; id: string; company: { id: string } }[]
    >([]);
    const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
    const [optionsCompany, setOptionsCompany] = useState([]);
    const [value, setValueTab] = React.useState(0);
    const [dataVerifiedEmail, setDataVerifiedEmail] = React.useState({
        open: false,
        id: '',
    });

    const { user } = useAuth();

    const handleChange = (event: any, newValue: number) => {
        setValueTab(newValue);
    };

    useEffect(() => {
        async function loadRolesByCompanyId(companyId: string) {
            const response = await api.get(`role?company.id=${companyId}`);
            setOptionsRole(response.data.data);
        }

        if (params && params.id) {
            setOpenLoading(true);
            api.get(`users/${params.id}`)
                .then(response => {
                    if (response.data.roles.length > 0) {
                        loadRolesByCompanyId(response.data.roles[0].company.id);
                    }
                    setModel(response.data);

                    setOpenLoading(false);
                })
                .catch(e => {
                    console.error(e);
                    setOpenLoading(false);
                    addToast({
                        type: 'error',
                        title: message.error.selectOne,
                    });
                });
        }
        api.get(`company`)
            .then(response => {
                setOptionsCompany(response.data.data);
            })
            .catch(error => {
                console.error(error);
                const { messageResponse, typeFeedback } =
                    handleExceptionMessage(error);
                addToast({
                    type: typeFeedback,
                    title: message.error.selectAll,
                    description: messageResponse,
                });
            });
    }, []);

    const { handleSubmit, control, reset, setValue, watch, getValues } =
        useForm<IFormInput>({
            defaultValues,
        });

    const setModel = (data: any) => {
        setValue('name', data.name);
        setValue('userName', data.userName);
        setValue('email', data.email);
        setValue('typeUser', typeUserInstance.getObject(data.typeUser));
        if (data.roles.length > 0) {
            setValue('roles', data.roles);
            setValue(
                'company',
                data.roles.map((r: any) => r.company),
            );
        }
        setValue('active', data.active);
        setValue('verifiedEmail', data.verifiedEmail);
    };

    const autocompleteSettings = {
        role: {
            query: (value: string) => `role?company.id.array=${value}`,
            setOptions: (options: []) => {
                const data = options.map((d: any) => {
                    return {
                        id: d.id,
                        description: d.description,
                        company: d?.company,
                    };
                });
                setOptionsRole(data);
            },
        },
    };

    const updateAutocomplete = async ({
        ids,
        keyField,
    }: IUpdateAutocomplete) => {
        if (ids && ids.length > 0) {
            setLoadingAutocomplete(true);
            const response = await api.get(
                autocompleteSettings[keyField].query(ids.join(',')),
            );
            autocompleteSettings[keyField].setOptions(
                response.data.data.map((d: any) => ({ ...d })),
            );
            setLoadingAutocomplete(false);
        }
    };

    const onChangeSetValues = (
        event: React.ChangeEvent<HTMLInputElement>,
        option: any,
    ) => {
        if (Array.isArray(option) && option.length > 0) {
            let keyField: 'role' = 'role';

            updateAutocomplete({ ids: option.map((o: any) => o.id), keyField });
        }
    };

    function renderAllInputsCompanyTabs(companyId: string) {
        const inputsForm: FormInputProps<IFormInput>[] = [
            {
                typeInput: 'autocomplete',
                name: 'roles',
                control: control,
                label: 'Funções/Papéis do usuário',
                options: optionsRole.filter(r => r.company?.id === companyId),
                setValue: setValue,
                loadingAutocomplete: loadingAutocomplete,
                multiple: true,
                md: 12,
                xs: 12,
                rules: {
                    validate: {
                        valideRequired: (val: any) => {
                            if (
                                watch('typeUser')?.id !== 'admin' &&
                                val?.length === 0
                            ) {
                                return false;
                            }
                            return true;
                        },
                    },
                },
                messagesError: [
                    {
                        type: 'valideRequired',
                        message: 'O campo funções é obrigatório',
                    },
                ],
                activeDebounce: true,
            },
        ];

        return inputsForm;
    }

    const inputsForm: FormInputProps<IFormInput>[] = [
        {
            typeInput: 'text',
            name: 'name',
            control: control,
            label: 'Nome',
            md: 6,
            xs: 12,
            rules: {
                required: true,
                minLength: 5,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo nome é obrigatório',
                },
                {
                    type: 'minLength',
                    message: 'O campo nome tem que ter mais do que 5 letras',
                },
            ],
        },
        {
            typeInput: 'text',
            name: 'userName',
            control: control,
            label: 'Login',
            md: 6,
            xs: 12,
        },
        {
            typeInput: 'text',
            name: 'email',
            control: control,
            label: 'Email',
            md: 6,
            xs: 12,
            rules: {
                required: true,
                pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo email é obrigatório',
                },
                {
                    type: 'pattern',
                    message: 'O email está inválido. Ex.: email@email.com',
                },
            ],
        },

        {
            typeInput: 'autocomplete',
            multiple: true,
            name: 'company',
            control: control,
            label: 'Empresa',
            options: optionsCompany,
            setValue: setValue,
            loadingAutocomplete: loadingAutocomplete,
            md: 6,
            xs: 12,
            rules: {
                validate: (val: string) => {
                    if (
                        watch('typeUser')?.id !== 'admin' &&
                        (!val || val?.length === 0)
                    ) {
                        return 'O campo empresa é obrigatório';
                    }
                },
            },
            handleChange: onChangeSetValues,
            activeDebounce: true,
            query: (value: string) => `company?limit=${value}`,
        },
        // {
        //     typeInput: 'autocomplete',
        //     name: 'roles',
        //     control: control,
        //     label: 'Funções/Papéis do usuário',
        //     options: optionsRole,
        //     setValue: setValue,
        //     loadingAutocomplete: loadingAutocomplete,
        //     multiple: true,
        //     md: 12,
        //     xs: 12,
        //     rules: {
        //         validate: {
        //             valideRequired: (val: any) => {
        //                 if (
        //                     watch('typeUser')?.id !== 'admin' &&
        //                     val?.length === 0
        //                 ) {
        //                     return false;
        //                 }
        //                 return true;
        //             },
        //         },
        //     },
        //     messagesError: [
        //         {
        //             type: 'valideRequired',
        //             message: 'O campo funções é obrigatório',
        //         },
        //     ],
        //     activeDebounce: true,
        // },
        {
            typeInput: 'password',
            name: 'password',
            control: control,
            label: 'Senha',
            md: 5,
            xs: 12,
            rules: {
                pattern: /^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,15}$/,
            },
            messagesError: [
                {
                    type: 'pattern',
                    message:
                        'A senha tem que ter de 6 a 15 caracteres, ao menos uma letra e ao menos um número',
                },
            ],
        },
        {
            typeInput: 'password',
            name: 'passwordConfirmation',
            control: control,
            label: 'Confirmar senha',
            md: 4,
            xs: 12,
            rules: {
                validate: (val: string) => {
                    if (val && watch('password') != val) {
                        return 'As senhas não conferem';
                    }
                },
            },
        },
        {
            typeInput: 'checkbox',
            name: 'active',
            control: control,
            label: 'Ativo',
            md: 1,
            xs: 12,
        },
        {
            typeInput: 'checkbox',
            name: 'verifiedEmail',
            control: control,
            label: 'Email verificado',
            md: 2,
            xs: 12,
            noRender: user?.typeUser === 'user',
        },
        {
            typeInput: 'autocomplete',
            name: 'typeUser',
            control: control,
            label: 'Tipo de usuário',
            options: typeUserEnum,
            setValue: setValue,
            loadingAutocomplete: false,
            md: 4,
            xs: 12,
            rules: {
                required: true,
            },
            messagesError: [
                {
                    type: 'required',
                    message: 'O campo tipo de usuário é obrigatório',
                },
            ],
            noRender: user?.typeUser === 'user',
        },
        {
            typeInput: 'component',
            name: 'accordion',
            control: control,
            label: '',
            md: 12,
            xs: 12,
            componentProps: {
                inputs: [],
                componentDataInputs: inputs => {
                    if (Array.isArray(getValues('company'))) {
                        return (
                            <>
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="simple tabs example">
                                    {getValues('company').map(item => {
                                        return (
                                            <Tab
                                                label={item.description}
                                                key={item.id}
                                            />
                                        );
                                    })}
                                </Tabs>
                                {getValues('company').map(
                                    (item: any, index: number) => {
                                        console.log(
                                            'test ',
                                            renderAllInputsCompanyTabs(item.id),
                                        );
                                        return (
                                            <TabPanel
                                                value={value}
                                                index={index}
                                                key={item.id}>
                                                <RenderForm
                                                    inputsForm={renderAllInputsCompanyTabs(
                                                        item.id,
                                                    )}
                                                />
                                            </TabPanel>
                                        );
                                    },
                                )}
                            </>
                            // <Accordion
                            //     expanded={expanded.roles}
                            //     onChange={() => {
                            //         setExpanded(prev => ({
                            //             ...prev,
                            //             roles: !prev.roles,
                            //         }));
                            //     }}>
                            //     <AccordionSummary
                            //         expandIcon={<ExpandMoreIcon />}
                            //         aria-controls="panel1a-content"
                            //         id="panel1a-header">
                            //         <Typography>
                            //             Funções/Papéis do usuário
                            //         </Typography>
                            //     </AccordionSummary>
                            //     <AccordionDetails>
                            //         <Grid container spacing={2}>
                            //             <RenderForm inputsForm={inputs} />
                            //         </Grid>
                            //     </AccordionDetails>
                            // </Accordion>
                        );
                    }
                },
                conditionRender: true,
            },
        },
    ];

    const submit = async (data: any) => {
        try {
            setOpenLoading(true);
            const typeUserId = data.typeUser.id;
            delete data.typeUser;
            data.typeUser = typeUserId;

            let response: any = null;
            if (params && params.id) {
                response = await api.patch(`users/${params.id}`, data);
            } else {
                response = await api.post('users', data);
            }
            addToast({
                type: 'success',
                title: message.success.save,
                description: '',
            });
            setOpenLoading(false);

            if (!response?.data?.verifiedEmail) {
                setDataVerifiedEmail(prev => ({
                    ...prev,
                    open: true,
                    id: response?.data?.id,
                }));
            } else {
                reset(defaultValues);
                history.goBack();
            }
        } catch (error) {
            setOpenLoading(false);
            const { messageResponse, typeFeedback } =
                handleExceptionMessage(error);
            addToast({
                type: typeFeedback,
                title: message.error.save,
                description: messageResponse,
            });
            console.error(error);
        }
    };

    const handleCancel = () => {
        history.goBack();
    };

    const handleDelete = async (id: string) => {
        setOpenLoading(true);

        try {
            await api.delete(`users/${id}`);
            addToast({
                type: 'success',
                title: message.success.delete,
                description: '',
            });
            setOpenLoading(false);
            history.push(`${paths.user}?`);
        } catch (error: any) {
            const { messageResponse, typeFeedback } =
                handleExceptionMessage(error);
            setOpenLoading(false);
            addToast({
                type: typeFeedback,
                title: message.error.delete,
                description: messageResponse,
            });
        }
    };

    const handleConfirmeDelete = async (confirm: boolean) => {
        setOpenModalDelete(false);
        if (confirm) {
            handleDelete(params.id);
        }
    };

    const handleConfirmSendVerifiedEmail = async (confirm: boolean) => {
        if (confirm) {
            setOpenLoading(true);
            try {
                await api.post('auth/confirm-email', {
                    id: dataVerifiedEmail.id,
                });
                addToast({
                    type: 'success',
                    title: message.success.save,
                    description: '',
                });
                reset(defaultValues);
                history.goBack();
            } catch (error) {
                const { messageResponse, typeFeedback } =
                    handleExceptionMessage(error);
                addToast({
                    type: typeFeedback,
                    title: message.error.save,
                    description: messageResponse,
                });
                console.error(error);
            }
        }
    };

    const handleConfirmSendVerifiedEmailClose = () => {
        setDataVerifiedEmail(prev => ({ ...prev, open: false }));
        reset(defaultValues);
        history.goBack();
    };

    return (
        <div className="principal-container">
            <AlertDialog
                handleConfirmation={handleConfirmeDelete}
                open={openModalDelete}
            />
            <AlertDialog
                handleConfirmation={handleConfirmSendVerifiedEmail}
                open={dataVerifiedEmail.open}
                messageDetail="Email não verificado, deseja enviar um email de confirmação?"
                messageDialogTitle="Email não verificado"
                handleCloseProps={handleConfirmSendVerifiedEmailClose}
            />
            <Paper component={'div'} sx={{ pt: 2, pl: 2, pb: 2, pr: 2 }}>
                <div className="principal-container-form-header">
                    <div className="principal-container-form-header-title">
                        <Typography variant="h6" id="tableTitle" component="h1">
                            {settings.title}
                        </Typography>
                    </div>
                    <div>
                        {params.id && (
                            <Tooltip title={'Excluir'}>
                                <IconButton
                                    onClick={() => {
                                        setOpenModalDelete(true);
                                    }}>
                                    <IconComponent
                                        icon={icons.Delete}
                                        color="error"
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                </div>
                <form onSubmit={handleSubmit(data => submit(data))}>
                    <Grid container spacing={2}>
                        <RenderForm
                            inputsForm={inputsForm.filter(i => !i?.noRender)}
                        />
                    </Grid>
                    <br />

                    <Stack spacing={1} direction="row">
                        <FormButton label={'Salvar'} typeButton={'submit'} />
                        <FormButton
                            label={'Voltar'}
                            typeButton={'cancel'}
                            onClick={() => handleCancel()}
                        />
                    </Stack>
                </form>
            </Paper>
        </div>
    );
};

export default Form;
